import { MouseEvent, useMemo } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import TagIcon from '@mui/icons-material/Tag';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { actionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { minutesToHours } from 'src/helpers/dateTimeFormatters';
import { ECheckinTimerType, ICheckin } from 'src/interfaces/Checkin';

import CheckinTimeCell from './CheckinTimeCell';

interface Props {
  checkins: ICheckin[];
  distanceId: string;
  onClickTimer: (id: string, type: ECheckinTimerType, timeStamp?: Date) => void;
  onSetDnf: (id: string, dnf: boolean) => void;
  onDeleteCheckin: (checkinId: string, e?: MouseEvent<unknown>) => void;
  onEditCheckin: (checkinId: string, e?: MouseEvent<unknown>) => void;
}

const CompetitionTable = ({
  checkins,
  distanceId,
  onClickTimer,
  onSetDnf,
  onDeleteCheckin,
  onEditCheckin,
}: Props) => {
  const { t } = useTranslation();

  const rows = useMemo(
    () => checkins.filter(c => c.participant),
    [checkins],
  );

  const columns = useMemo(
    (): GridColDef<ICheckin>[] => [
      {
        field: 'ser',
        headerName: '',
        width: 85,
        sortable: false,
        renderHeader: () => <TagIcon sx={{ verticalAlign: 'middle' }} />,
        renderCell: ({ row }) => (
          <Typography sx={{ fontSize: 20, mr: 3 }} align="center">
            {row.ser}
          </Typography>
        ),
      },
      {
        field: 'contestantName',
        headerName: 'Név',
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: (_, row) =>
          row.participant ? `${row.participant.lastName} ${row.participant.firstName}` : '',
      },
      {
        field: 'startTime',
        headerName: 'Indulás',
        width: 220,
        sortable: false,
        renderCell: ({ row }) => (
          <CheckinTimeCell
            checkinId={row.id}
            field="start"
            startTime={row.startTime}
            finishTime={row.finishTime}
            onClickTimer={onClickTimer}
          />
        ),
      },
      {
        field: 'finishTime',
        headerName: 'Érkezés',
        width: 220,
        sortable: false,
        renderCell: ({ row }) => (
          <CheckinTimeCell
            checkinId={row.id}
            field="finish"
            startTime={row.startTime}
            finishTime={row.finishTime}
            dnf={row.dnf}
            onClickTimer={onClickTimer}
            onSetDnf={onSetDnf}
          />
        ),
      },
      {
        field: 'duration',
        headerName: 'Idő',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => (row.duration ? minutesToHours(row.duration, t) : '-'),
      },
      actionsColumn<ICheckin>(t, 'competitions.optionsLabel', ({ row }) => (
        <>
          <Tooltip title={t('checkins.deleteCheckin')}>
            <IconButton size="small" onClick={e => onDeleteCheckin(row.id, e)}>
              <UnpublishedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('checkins.editCheckin')}>
            <IconButton size="small" onClick={e => onEditCheckin(row.id, e)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('timers.resetTimer')}>
            <IconButton
              aria-label={t('timers.resetTimer')}
              onClick={e => {
                e.stopPropagation();
                onClickTimer(row.id, ECheckinTimerType.clear);
              }}
              edge="end"
            >
              <TimerOffIcon />
            </IconButton>
          </Tooltip>
        </>
      )),
    ],
    [onClickTimer, onSetDnf, onDeleteCheckin, onEditCheckin, t],
  );

  return (
    <DashboardDataGrid<ICheckin>
      key={distanceId}
      aria-label={t('competitions.competitionsTableLabel')}
      chrome="standard"
      rows={rows}
      columns={columns}
    />
  );
};

export default CompetitionTable;
